'use client';

import { c as _c } from "react/compiler-runtime";
import { PreviewModeToggle } from '@/components/header/preview-mode-toggler';
import { useCurrentLocale } from '@/hooks/use-current-locale';
import type { Locale, StaticTranslationString } from '@/types/types';
import { i18nConfig } from '@/types/types';
import { PREVIEW_SESSION_COOKIE } from '@/utils/preview-session-cookie';
import Cookies from 'js-cookie';
import { RefreshCw, X } from 'lucide-react';
import type { User } from 'next-auth';
import { useSearchParams } from 'next/navigation';
import React, { useState } from 'react';
interface PreviewModeBannerProperties {
  user: User | undefined;
  canAccessAdmin: boolean;
}
interface PreviewModeBannerTranslationStrings {
  account: StaticTranslationString;
  anonymous: StaticTranslationString;
  previewMode: StaticTranslationString;
  exitPreviewMode: StaticTranslationString;
}

/**
 *
 * The preview mode banner is a small gray banner at the top of the screen and
 * is visible whenever the user is viewing at a page in preview mode.
 * The preview banner is part of the previewing system.
 *
 */
export const PreviewModeBanner: React.FC<PreviewModeBannerProperties> = t0 => {
  const $ = _c(34);
  const {
    user,
    canAccessAdmin
  } = t0;
  const locale = useCurrentLocale(i18nConfig) as Locale;
  const searchParameters = useSearchParams();
  const [isReloading, setIsReloading] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setIsMounted(true);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  React.useEffect(t1, t2);
  if (!isMounted) {
    let t3;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <></>;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    return t3;
  }
  const tokenParameter = searchParameters.get("preview-token");
  const accessWithToken = tokenParameter !== null && tokenParameter.length > 0;
  const renderPreviewModeBanner = accessWithToken || canAccessAdmin;
  if (!renderPreviewModeBanner || isDismissed) {
    let t3;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <></>;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    return t3;
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      account: {
        de: "Account: ",
        en: "Account: ",
        fr: "Compte: "
      },
      anonymous: {
        de: "anonym",
        en: "anonymous",
        fr: "anonyme"
      },
      previewMode: {
        de: "Vorschau:",
        en: "Preview:",
        fr: "Aper\xE7u:"
      },
      exitPreviewMode: {
        de: "Vorschau verlassen",
        en: "Exit preview mode",
        fr: "Quitter le mode aper\xE7u"
      }
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const staticTranslationStrings = t3;
  let t4;
  if ($[5] !== locale || $[6] !== user) {
    t4 = user ?? {
      email: staticTranslationStrings.anonymous[locale]
    };
    $[5] = locale;
    $[6] = user;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    email: userEmail
  } = t4;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setIsReloading(true);
      globalThis.location.reload();
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleReload = t5;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      Cookies.remove(PREVIEW_SESSION_COOKIE, {
        path: "/"
      });
      setIsDismissed(true);
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const handleDismiss = t6;
  const t7 = staticTranslationStrings.account[locale];
  let t8;
  if ($[10] !== t7) {
    t8 = <span className="text-gray-300 max-sm:hidden">{t7}</span>;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== userEmail) {
    t9 = <span className="ml-1 font-medium">{userEmail}</span>;
    $[12] = userEmail;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t8 || $[15] !== t9) {
    t10 = <span className="flex items-center text-xs text-gray-100">{t8}{t9}</span>;
    $[14] = t8;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const t11 = staticTranslationStrings.previewMode[locale];
  let t12;
  if ($[17] !== t11) {
    t12 = <span className="text-xs text-gray-300">{t11}</span>;
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <PreviewModeToggle />;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== t12) {
    t14 = <div className="flex items-center space-x-2">{t12}{t13}</div>;
    $[20] = t12;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  const t15 = `h-4 w-4 cursor-pointer text-gray-300 ${isReloading ? "animate-spin" : ""}`;
  let t16;
  if ($[22] !== t15) {
    t16 = <RefreshCw className={t15} onClick={handleReload} />;
    $[22] = t15;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  const t17 = staticTranslationStrings.exitPreviewMode[locale];
  let t18;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <X className="h-4 w-4 cursor-pointer text-gray-300 hover:text-white" />;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] !== t17) {
    t19 = <button onClick={handleDismiss} aria-label={t17}>{t18}</button>;
    $[25] = t17;
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  let t20;
  if ($[27] !== t16 || $[28] !== t19) {
    t20 = <div className="flex flex-row gap-x-4">{t16}{t19}</div>;
    $[27] = t16;
    $[28] = t19;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  let t21;
  if ($[30] !== t10 || $[31] !== t14 || $[32] !== t20) {
    t21 = <div data-preview-banner={true} className="relative z-[200] flex h-(--preview-banner-height) items-center justify-between overflow-hidden bg-gray-900 px-4 md:px-8">{t10}{t14}{t20}</div>;
    $[30] = t10;
    $[31] = t14;
    $[32] = t20;
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  return t21;
};